// src/components/BucksAdminInventoryProductAddModal.jsx

import React, { useState, useEffect, useRef } from 'react';
import '../stylings/BucksAdminInventoryProductAddModal.css';

// Define API endpoints
const ADD_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-product-add';
const EDIT_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-product-edit';

const BucksAdminInventoryProductAddModal = ({
  show,
  onClose,
  onSave,
  editingItem = null,
  categories
}) => {
  /* ───────────────────────────────────────── STATE ───────────────────────────────────────── */
  // Animation
  const [animationClass, setAnimationClass] = useState('');
  const modalRef          = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    id: '',
    product_code: '',
    name: '',
    description: '',
    category: 'Fruit',
    price: '',
    stock: '',
    unit: 'lb',
    status: 'In Stock',
    season_status: 'In Season',
    main_image_id: null,

    /*  ⬇︎ NEW image-related fields we'll send to the API  */
    image_base64: '',
    image_filename: '',
    image_content_type: '',
    is_primary: 1,

    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null
  });

  // Success view
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedProduct, setSavedProduct] = useState(null);

  // Image preview
  const [previewImage, setPreviewImage] = useState('');
  const fileInputRef = useRef(null);

  // Misc
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  /* ───────────────────────────────────────── CONSTANTS ────────────────────────────────────── */
  const units           = ['lb', 'oz', 'pint', 'bunch', 'jar', 'loaf', 'dozen', 'each'];
  const statuses        = ['In Stock', 'Low Stock', 'Out of Stock'];
  const seasonStatuses  = ['In Season', 'Out of Season'];

  /* ──────────────────────────────────────── EFFECTS ───────────────────────────────────────── */
  // Populate / reset form when editingItem changes
  useEffect(() => {
    if (editingItem) {
      // Use a callback to ensure all fields are set atomically
      setFormData(f => ({
        ...f,
        ...editingItem,
        product_code: editingItem.product_code || editingItem.id || '',
        image_base64: '',
        image_filename: '',
        image_content_type: '',
        is_primary: 1,
        // Explicitly preserve the status from the database
        status: editingItem.status
      }));

      // Show existing preview (first image, if any)
      setPreviewImage(
        editingItem.images?.[0]?.image_url ||
        editingItem.image_url            ||
        ''
      );
    } else {
      resetForm();
      setPreviewImage('');
    }

    setShowSuccess(false);
    setSavedProduct(null);
  }, [editingItem]);

  // Handle modal enter / exit animations
  useEffect(() => {
    if (show) {
      setAnimationClass('bucks-admin-modal-entering');
      document.body.style.overflow = 'hidden';

      const t = setTimeout(() => setAnimationClass('bucks-admin-modal-entered'), 50);
      return () => clearTimeout(t);
    }

    if (!show) {
      setAnimationClass('bucks-admin-modal-exiting');

      const t = setTimeout(() => {
        setAnimationClass('');
        document.body.style.overflow = '';
      }, 300);

      return () => clearTimeout(t);
    }
  }, [show]);

  // Handle unit change to "each" - preserve manual status selection
  useEffect(() => {
    if (formData.unit === 'each' && formData.stock !== -1) {
      // Only update if stock isn't already set to -1 (meaning this isn't from editing an existing product)
      setFormData(prev => ({
        ...prev,
        stock: -1, // Use -1 to represent "infinite"
        // Only set status to "In Stock" if no status was manually selected AND this is a new product
        ...((!editingItem && (!prev.status || prev.status === '')) && { status: 'In Stock' })
      }));
    }
  }, [formData.unit, editingItem]);

  /* ──────────────────────────────────────── HELPERS ───────────────────────────────────────── */
  const resetForm = () => {
    setFormData({
      id: '',
      product_code: '',
      name: '',
      description: '',
      category: 'Fruit',
      price: '',
      stock: '',
      unit: 'lb',
      status: 'In Stock',
      season_status: 'In Season',
      main_image_id: null,
      image_base64: '',
      image_filename: '',
      image_content_type: '',
      is_primary: 1,
      created_by: null,
      created_at: null,
      updated_by: null,
      updated_at: null
    });
    setPreviewImage('');
    setErrorMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const generateProductCode = category => {
    const prefix = category.charAt(0).toLowerCase();
    const ts     = Date.now().toString().slice(-4);
    return `${prefix}${ts}`;
  };

  const formatCurrency = v => `$${parseFloat(v).toFixed(2)}`;

  /* ───────────────────────────────────────── EVENTS ───────────────────────────────────────── */
  const handleOutsideClick = e => {
    if (modalRef.current && !modalRef.current.contains(e.target)) handleClose();
  };

  const handleInputChange = e => {
    const { name, value, type } = e.target;
    
    // Special handling for unit field
    if (name === 'unit') {
      if (value === 'each') {
        setFormData(f => ({
          ...f,
          [name]: value,
          stock: -1
          // Remove the forced status change - let user keep their selected status
        }));
      } else {
        // If changing from "each" to another unit, reset stock to 0
        setFormData(f => ({
          ...f,
          [name]: value,
          stock: f.unit === 'each' ? 0 : f.stock
        }));
      }
    } else {
      // Normal handling for other fields
      setFormData(f => ({
        ...f,
        [name]: type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
      }));
    }
  };

  // File → preview + base64
  const handleFileChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    /* show preview immediately */
    setPreviewImage(URL.createObjectURL(file));

    /* convert to base64 (without the data:... prefix) */
    const reader = new FileReader();
    reader.onload = () => {
      const [, data] = reader.result.split(',');
      setFormData(f => ({
        ...f,
        image_base64: data,
        image_filename: file.name,
        image_content_type: file.type,
        is_primary: 1
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPreviewImage('');
    setFormData(f => ({
      ...f,
      image_base64: '',
      image_filename: '',
      image_content_type: ''
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    setAnimationClass('bucks-admin-modal-exiting');
    const t = setTimeout(() => {
      onClose();
      setShowSuccess(false);
      setSavedProduct(null);
      if (!editingItem) resetForm();
    }, 300);
    return () => clearTimeout(t);
  };

  /* ───────────────────────────────────────── SUBMIT ───────────────────────────────────────── */
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    const productData = { ...formData };

    if (!productData.product_code)
      productData.product_code = generateProductCode(productData.category);

    // timestamps for UI display
    const nowISO = new Date().toISOString();
    const userId =
      JSON.parse(sessionStorage.getItem('adminUser'))?.id || 1;

    if (editingItem) {
      productData.updated_at = nowISO;
      productData.updated_by = userId;
    } else {
      productData.created_at = nowISO;
      productData.updated_at = nowISO;
      productData.created_by = userId;
      productData.updated_by = userId;
    }

    // IMPORTANT: For editing, always preserve the manually set status
    // Only auto-calculate status for new products or when status is explicitly empty
    if (editingItem) {
      // When editing, keep the status as-is from the form (user may have changed it)
      productData.status = formData.status;
    } else if (!formData.status || formData.status === '') {
      // Only auto-calculate for new products without a status
      if (productData.unit === 'each') {
        productData.status = 'In Stock'; // Default for "each" but can be overridden
      } else {
        productData.status =
          parseInt(productData.stock) === 0
            ? 'Out of Stock'
            : parseInt(productData.stock) < 10
            ? 'Low Stock'
            : 'In Stock';
      }
    }

    /* prepare payload for Lambda */
    const endpoint = editingItem ? EDIT_URL : ADD_URL;

    // build payload
    const payload = {
      // when editing, include the product id so lambda knows which record to update
      ...(editingItem && { id: formData.id }),
      product_code: productData.product_code.trim(),
      name: productData.name.trim(),
      description: (productData.description || '').trim(),
      category: productData.category.trim(),
      price: parseFloat(productData.price),
      stock: parseInt(productData.stock),
      unit: productData.unit.trim(),
      status: productData.status.trim(), // Include the status in payload
      season_status: productData.season_status.trim(),
      updated_by: productData.updated_by,
      // only send image fields if a new file was chosen
      ...(productData.image_base64 && {
        image_base64: productData.image_base64,
        image_filename: productData.image_filename || `product_${Date.now()}.jpg`,
        image_content_type: productData.image_content_type,
        is_primary: productData.is_primary,
        bucket_name: 'bucks-farmstand-product-images'
      })
    };

    if (!editingItem) {
      payload.created_by = productData.created_by;
    }

    try {
      const adminToken = sessionStorage.getItem('adminToken');
      if (!adminToken) throw new Error('Authentication required. Please log in again.');

      const controller = new AbortController();
      const tid        = setTimeout(() => controller.abort(), 60000);

      const res = await fetch(
        endpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization:  adminToken
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        }
      );
      clearTimeout(tid);

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to add product');

      productData.id            = result.product_id;
      productData.main_image_id = result.main_image_id;

      onSave(productData, null /* image already embedded */);
      setSavedProduct({ ...productData, imageUrl: previewImage });
      setShowSuccess(true);
    } catch (err) {
      if (err.name === 'AbortError')
        setErrorMessage('Request timed out. Try again with a smaller image.');
      else setErrorMessage(err.message || 'An error occurred while saving.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ───────────────────────────────────────── RENDER ───────────────────────────────────────── */
  if (!show && !animationClass) return null;

  return (
    <div
      className={`bucks-admin-modal-overlay ${animationClass}`}
      onClick={handleOutsideClick}
    >
      <div className="bucks-admin-modal-container" ref={modalRef}>
        {/* ────────────── Header ────────────── */}
        <div className="bucks-admin-modal-header">
          <h2>
            {showSuccess
              ? editingItem
                ? 'Product Updated'
                : 'Product Added'
              : editingItem
              ? 'Edit Product'
              : 'Add New Product'}
          </h2>
          <button
            className="bucks-admin-modal-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            {/* X icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* ────────────── Body ────────────── */}
        <div className="bucks-admin-modal-body">
          {showSuccess && savedProduct ? (
            /* Success View */
            <div className="bucks-admin-modal-success">
              <div className="bucks-admin-modal-success-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>

              <h3 className="bucks-admin-modal-success-title">
                {editingItem
                  ? 'Product Successfully Updated'
                  : 'Product Successfully Added'}
              </h3>
              <p className="bucks-admin-modal-success-message">
                {editingItem
                  ? `Your product "${savedProduct.name}" has been updated and is now ready for customers.`
                  : `Your product "${savedProduct.name}" has been added to your inventory and is now ready for customers.`}
              </p>

              <div className="bucks-admin-modal-product-summary">
                <div className="bucks-admin-modal-product-summary-image">
                  {savedProduct.imageUrl ? (
                    <img
                      src={savedProduct.imageUrl}
                      alt={savedProduct.name}
                      className="bucks-admin-modal-summary-img"
                    />
                  ) : (
                    <div className="bucks-admin-modal-summary-no-image">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <p>No image uploaded</p>
                    </div>
                  )}
                </div>

                <div className="bucks-admin-modal-product-summary-details">
                  <div className="bucks-admin-modal-summary-header">
                    <h4>{savedProduct.name}</h4>
                    <div className="bucks-admin-modal-summary-code">
                      {savedProduct.product_code}
                    </div>
                  </div>

                  <div className="bucks-admin-modal-summary-grid">
                    <div className="bucks-admin-modal-summary-item">
                      <div className="bucks-admin-modal-summary-label">Category</div>
                      <div className="bucks-admin-modal-summary-value">
                        {savedProduct.category}
                      </div>
                    </div>
                    <div className="bucks-admin-modal-summary-item">
                      <div className="bucks-admin-modal-summary-label">Price</div>
                      <div className="bucks-admin-modal-summary-value">
                        {formatCurrency(savedProduct.price)}
                      </div>
                    </div>
                    <div className="bucks-admin-modal-summary-item">
                      <div className="bucks-admin-modal-summary-label">Stock</div>
                      <div className="bucks-admin-modal-summary-value">
                        {savedProduct.stock} {savedProduct.unit}
                      </div>
                    </div>
                    <div className="bucks-admin-modal-summary-item">
                      <div className="bucks-admin-modal-summary-label">Status</div>
                      <div className="bucks-admin-modal-summary-value">
                        <span
                          className={`bucks-admin-modal-summary-status status-${savedProduct.status
                            .toLowerCase()
                            .replace(' ', '-')}`}
                        >
                          {savedProduct.status}
                        </span>
                      </div>
                    </div>
                    <div className="bucks-admin-modal-summary-item">
                      <div className="bucks-admin-modal-summary-label">Season</div>
                      <div className="bucks-admin-modal-summary-value">
                        <span
                          className={`bucks-admin-modal-summary-status status-${
                            savedProduct.season_status === 'In Season'
                              ? 'in-stock'
                              : 'out-of-stock'
                          }`}
                        >
                          {savedProduct.season_status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {savedProduct.description && (
                    <div className="bucks-admin-modal-summary-description">
                      <div className="bucks-admin-modal-summary-label">
                        Description
                      </div>
                      <div className="bucks-admin-modal-summary-value description">
                        {savedProduct.description}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bucks-admin-modal-success-actions">
                <button
                  type="button"
                  className="bucks-admin-modal-add-another-btn"
                  onClick={() => {
                    setShowSuccess(false);
                    resetForm();
                  }}
                >
                  {editingItem ? 'Edit Another Product' : 'Add Another Product'}
                </button>
                <button
                  type="button"
                  className="bucks-admin-modal-done-btn"
                  onClick={handleClose}
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* Form View */
            <form onSubmit={handleSubmit} className="bucks-admin-modal-form">
              <div className="bucks-admin-modal-columns">
                {/* ────────────── Column 1 ────────────── */}
                <div className="bucks-admin-modal-column">
                  {/* Product code / name */}
                  <div className="bucks-admin-modal-form-row">
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-code">Product Code</label>
                      <input
                        type="text"
                        id="product-code"
                        name="product_code"
                        value={formData.product_code}
                        onChange={handleInputChange}
                        placeholder="Auto-generated if empty"
                        disabled={editingItem}
                      />
                    </div>
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-name">Product Name*</label>
                      <input
                        type="text"
                        id="product-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Category / Price */}
                  <div className="bucks-admin-modal-form-row">
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-category">Category*</label>
                      <select
                        id="product-category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                      >
                        {categories.map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-price">Price*</label>
                      <div className="bucks-admin-modal-price-input-container">
                        <span className="bucks-admin-modal-price-symbol">$</span>
                        <input
                          type="number"
                          id="product-price"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Stock / Unit */}
                  <div className="bucks-admin-modal-form-row">
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-stock">Stock Quantity*</label>
                      <input
                        type="number"
                        id="product-stock"
                        name="stock"
                        value={formData.unit === 'each' ? 'Infinite' : formData.stock}
                        onChange={handleInputChange}
                        min="0"
                        required
                        disabled={formData.unit === 'each'}
                      />
                      {formData.unit === 'each' && (
                        <small className="bucks-admin-modal-helper-text">
                          Products with unit "each" have unlimited stock
                        </small>
                      )}
                    </div>
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-unit">Unit*</label>
                      <select
                        id="product-unit"
                        name="unit"
                        value={formData.unit}
                        onChange={handleInputChange}
                        required
                      >
                        {units.map(u => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Status / Season */}
                  <div className="bucks-admin-modal-form-row">
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-status">Status</label>
                      <select
                        id="product-status"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        {statuses.map(s => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <small className="bucks-admin-modal-helper-text">
                        Auto-set based on quantity, but can be overridden
                      </small>
                    </div>
                    <div className="bucks-admin-modal-form-group">
                      <label htmlFor="product-season-status">
                        Season Status
                      </label>
                      <select
                        id="product-season-status"
                        name="season_status"
                        value={formData.season_status}
                        onChange={handleInputChange}
                      >
                        {seasonStatuses.map(s => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bucks-admin-modal-form-group">
                    <label htmlFor="product-description">Description</label>
                    <textarea
                      id="product-description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                    ></textarea>
                  </div>
                </div>

                {/* ────────────── Column 2 (Image) ────────────── */}
                <div className="bucks-admin-modal-column">
                  <div className="bucks-admin-modal-form-group">
                    <label>Product Image</label>
                    <div className="bucks-admin-modal-image-upload">
                      {previewImage ? (
                        <div className="bucks-admin-modal-image-preview">
                          <img
                            src={previewImage}
                            alt="Preview"
                            className="bucks-admin-modal-preview-img"
                          />
                          <button
                            type="button"
                            className="bucks-admin-modal-remove-image-btn"
                            onClick={handleRemoveImage}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="bucks-admin-modal-image-placeholder">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          <p>Upload product image</p>
                        </div>
                      )}
                      <input
                        type="file"
                        id="product-image"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="bucks-admin-modal-file-input"
                        ref={fileInputRef}
                      />
                      <button
                        type="button"
                        className="bucks-admin-modal-upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {previewImage ? 'Change Image' : 'Select Image'}
                      </button>
                      <div className="bucks-admin-modal-helper-text">
                        Recommended size: 800×600 px, max ≈5 MB
                      </div>
                    </div>
                  </div>

                  {/* Metadata for existing items */}
                  {editingItem && (
                    <div className="bucks-admin-modal-metadata">
                      <h3>Product Metadata</h3>
                      <div className="bucks-admin-modal-metadata-item">
                        <span>Created By:</span>
                        <span>Admin User</span>
                      </div>
                      <div className="bucks-admin-modal-metadata-item">
                        <span>Created At:</span>
                        <span>
                          {formData.created_at
                            ? new Date(formData.created_at).toLocaleString()
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="bucks-admin-modal-metadata-item">
                        <span>Last Updated:</span>
                        <span>
                          {formData.updated_at
                            ? new Date(formData.updated_at).toLocaleString()
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Error message */}
              {errorMessage && (
                <div className="bucks-admin-modal-error">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12" y2="16"></line>
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Actions */}
              <div className="bucks-admin-modal-form-actions">
                <button
                  type="button"
                  className="bucks-admin-modal-cancel-btn"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bucks-admin-modal-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      <span>{editingItem ? 'Updating…' : 'Adding…'}</span>
                    </>
                  ) : editingItem ? (
                    'Update Product'
                  ) : (
                    'Add Product'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BucksAdminInventoryProductAddModal;